import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
} from "@mui/material";
import {
  People as PeopleIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import api from "../services/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCategories: 0,
    totalProducts: 0,
    activeProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    conversionRate: 0,
    avgOrderValue: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Chart data
  const [revenueData, setRevenueData] = useState([]);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [categorySalesData, setCategorySalesData] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [
        usersResponse,
        ordersResponse,
        orderStatsResponse,
        categoriesResponse,
        productsResponse,
        activeProductsResponse,
      ] = await Promise.all([
        api.get("/users", {
          params: { page: 1, limit: 100, sort: "createdAt", order: "desc" },
        }),
        api.get("/orders/admin/all", {
          params: { page: 1, limit: 100, sortBy: "invoiceGeneratedAt", sortOrder: "desc" },
        }),
        api.get("/orders/admin/stats"),
        api.get("/categories", { params: { page: 1, limit: 1 } }),
        api.get("/products", {
          params: { page: 1, limit: 100, sort: "createdAt", order: "desc" },
        }),
        api.get("/products", {
          params: { page: 1, limit: 1, isActive: "true" },
        }),
      ]);

      const usersData = usersResponse.data?.users ?? [];
      const usersPagination = usersResponse.data?.pagination ?? {};

      const ordersData = ordersResponse.data?.orders ?? [];
      const ordersPagination = ordersResponse.data?.pagination ?? {};

      const orderStats = orderStatsResponse.data?.stats ?? {};

      const categoriesPagination =
        categoriesResponse.data?.pagination ?? {};

      const productsData = productsResponse.data?.products ?? [];
      const productsPagination = productsResponse.data?.pagination ?? {};

      const activeProductsPagination =
        activeProductsResponse.data?.pagination ?? {};

      const totalOrders = orderStats.totalOrders ?? ordersPagination.totalOrders ?? 0;
      const totalRevenue = orderStats.totalRevenue ?? 0;
      const deliveredOrders = orderStats.deliveredOrders ?? 0;

      const computedConversionRate =
        totalOrders > 0 ? (deliveredOrders / totalOrders) * 100 : 0;
      const computedAvgOrderValue =
        totalOrders > 0 ? totalRevenue / totalOrders : 0;

      setStats({
        totalUsers: usersPagination.totalUsers ?? usersData.length ?? 0,
        totalCategories: categoriesPagination.totalCategories ?? 0,
        totalProducts: productsPagination.totalProducts ?? productsData.length ?? 0,
        activeProducts: activeProductsPagination.totalProducts ?? 0,
        totalOrders,
        totalRevenue,
        conversionRate: Number(computedConversionRate.toFixed(1)),
        avgOrderValue: Number(computedAvgOrderValue.toFixed(2)),
      });

      const recentUsersData = usersData.slice(0, 5).map((user) => ({
        id: user._id,
        name:
          [user.firstName, user.lastName].filter(Boolean).join(" ") ||
          user.email ||
          "Unnamed User",
        email: user.email,
        createdAt: user.createdAt
          ? new Date(user.createdAt).toLocaleDateString("en-IN", {
              timeZone: "Asia/Kolkata"
            })
          : "-",
        status: user.status || "active",
      }));
      setRecentUsers(recentUsersData);

      const recentOrdersData = ordersData.slice(0, 5).map((order) => ({
        id: order.id,
        customer: order.customer?.name || "Unknown",
        amount: Number(order.total || order.grandTotal || 0),
        status: order.status || "pending",
        date: order.invoiceGeneratedAt
          ? new Date(order.invoiceGeneratedAt).toLocaleDateString("en-IN", {
              timeZone: "Asia/Kolkata"
            })
          : "-",
      }));
      setRecentOrders(recentOrdersData);

      // Revenue & Orders trend by month (last 6 months)
      const revenueTimelineMap = new Map();
      ordersData.forEach((order) => {
        if (!order.createdAt) return;
        const orderDate = new Date(order.createdAt);
        if (Number.isNaN(orderDate.getTime())) {
          return;
        }
        const key = `${orderDate.getFullYear()}-${orderDate.getMonth()}`;
        if (!revenueTimelineMap.has(key)) {
          revenueTimelineMap.set(key, { revenue: 0, orders: 0, date: new Date(orderDate.getFullYear(), orderDate.getMonth(), 1) });
        }
        const entry = revenueTimelineMap.get(key);
        entry.revenue += Number(order.total || order.grandTotal || 0);
        entry.orders += 1;
      });

      const revenueTimeline = Array.from(revenueTimelineMap.values())
        .sort((a, b) => a.date - b.date)
        .slice(-6)
        .map((entry) => ({
          name: entry.date.toLocaleString("en-IN", {
            month: "short",
            timeZone: "Asia/Kolkata"
          }) + ` ${entry.date.getFullYear()}`,
          revenue: Number(entry.revenue.toFixed(2)),
          orders: entry.orders,
        }));
      setRevenueData(revenueTimeline);

      // User growth by month (last 6 months)
      const userGrowthMap = new Map();
      usersData.forEach((user) => {
        if (!user.createdAt) return;
        const joinedDate = new Date(user.createdAt);
        if (Number.isNaN(joinedDate.getTime())) {
          return;
        }
        const key = `${joinedDate.getFullYear()}-${joinedDate.getMonth()}`;
        if (!userGrowthMap.has(key)) {
          userGrowthMap.set(key, { users: 0, date: new Date(joinedDate.getFullYear(), joinedDate.getMonth(), 1) });
        }
        const entry = userGrowthMap.get(key);
        entry.users += 1;
      });

      const userGrowthTimeline = Array.from(userGrowthMap.values())
        .sort((a, b) => a.date - b.date)
        .slice(-6)
        .map((entry) => ({
          name: entry.date.toLocaleString("en-IN", {
            month: "short",
            timeZone: "Asia/Kolkata"
          }) + ` ${entry.date.getFullYear()}`,
          users: entry.users,
        }));
      setUserGrowthData(userGrowthTimeline);

      // Category "sales" data based on product count per category
      const categoryCountMap = new Map();
      productsData.forEach((product) => {
        const categoryName = product.categoryId?.title || "Uncategorized";
        const currentCount = categoryCountMap.get(categoryName) ?? 0;
        categoryCountMap.set(categoryName, currentCount + 1);
      });
      const categorySales = Array.from(categoryCountMap.entries())
        .map(([name, count]) => ({ name, sales: count }))
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 8);
      setCategorySalesData(categorySales);

    } catch (error) {
      console.error("Failed to load dashboard", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch dashboard data"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const StatCard = ({
    title,
    value,
    icon,
    color = "primary",
    trend,
    trendValue,
    subtitle,
  }) => (
    <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={`${color}.main`}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
      case "delivered":
        return "success";
      case "pending":
      case "shipped":
        return "warning";
      case "cancelled":
      case "inactive":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Box textAlign="center">
          <LinearProgress sx={{ width: 200, mb: 2 }} />
          <Typography>Loading dashboard...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
          Dashboard Overview
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDashboardData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers.toLocaleString()}
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            color="primary"
            trend="up"
            // trendValue="+12%"
            subtitle="Active users"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={stats.totalOrders.toLocaleString()}
            icon={<ShoppingCartIcon sx={{ fontSize: 40 }} />}
            color="success"
            trend="up"
            // trendValue="+8%"
            subtitle="This month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue"
            value={`₹${stats.totalRevenue.toLocaleString()}`}
            icon={<Typography sx={{ fontSize: 40, fontWeight: 'bold' }}>₹</Typography>}
            color="warning"
            trend="up"
            // trendValue="+15%"
            subtitle="Total revenue"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Categories"
            value={stats.totalCategories.toLocaleString()}
            icon={<CategoryIcon sx={{ fontSize: 40 }} />}
            color="info"
            trend="up"
            // trendValue="+2.1%"
            subtitle={`Active products: ${stats.activeProducts.toLocaleString()}`}
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue & Orders Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stackId="1"
                    stroke="#8884d8"
                    fill="#8884d8"
                  />
                  <Area
                    type="monotone"
                    dataKey="orders"
                    stackId="2"
                    stroke="#82ca9d"
                    fill="#82ca9d"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* User Growth Chart */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Growth
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#8884d8"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sales by Category
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={categorySalesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="sales" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity & Tables */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" component="h3">
                  Recent Users
                </Typography>
                <Button
                  size="small"
                  startIcon={<ViewIcon />}
                  onClick={() => navigate("/users")}
                >
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                      <TableCell>Joined</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          color={getStatusColor(user.status)}
                          size="small"
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                        <TableCell>{user.createdAt}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" component="h3">
                  Recent Orders
                </Typography>
                <Button
                  size="small"
                  startIcon={<ViewIcon />}
                  onClick={() => navigate("/orders")}
                >
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.customer}</TableCell>
                      <TableCell>
                        {order.amount.toLocaleString("en-IN", {
                          style: "currency",
                          currency: "INR",
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status}
                          color={getStatusColor(order.status)}
                          size="small"
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                        <TableCell>{order.date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
